import moment from 'moment';
import { ColumnType, InventoryEventLabels, InventoryEntities, OsType, AgentInventoryEventLabels, ExcludedValues, InstallStatesValues, OperationType } from './enums';
import { HtmlEscapeUtils } from './HtmlEscapeUtils';

const ApiEndpoints = {
  TENANTS: 'api/tenants',
  INVENTORY_ORGS_COUNT: 'api/inventory/organizations/count',
  INVENTORY_SITES_COUNT: 'api/inventory/sites/count',
  INVENTORY_GROUPS_COUNT: 'api/inventory/groups/count',
  INVENTORY_ORGS: 'api/inventory/organizations',
  INVENTORY_SITES: 'api/inventory/sites',
  INVENTORY_GROUPS: 'api/inventory/groups',
  INVENTORY_AGENTS: 'api/inventory/agents',
  INVENTORY_AGENTS_COUNT: 'api/inventory/agents/count',
  INVENTORY_ONDEMAND_SYNC: 'api/inventory/ondemandsync',
  INVENTORY_TASK_RUN: 'api/inventory/nextrun',
  TASK_RESULTS: 'api/taskresults',
  TENANTS_UPDATE_API_DETAILS: 'api/tenants/updateapidetails',
  INVENTORY_EVENTS: 'api/inventory/events',
  AGENT_ONDEMAND_SYNC: 'api/endpoints/ondemandsync',
  AGENT_UNINSTALL: 'api/endpoints/uninstall',
  AGENT_EVENTS: 'api/agents/events',
  AGENT_TASK_SCAN: 'api/endpoints/scantask',
  AGENT_TASK_RUN: 'api/agents/tasks/nextrun',
  COMPANY_EXCLUSION: 'api/companies/exclusions',
  GROUP_EXCLUSION: 'api/groups/exclusions',
  ASSETS: 'api/inventory/assets',
  AGENT_EXCLUSION: 'api/agents/exclusions',
  AGENT_INSTALL_STATES: 'api/agents/installstates',
  DASHBOARD_DATA: 'api/dashboard',
  AUTHORIZATION: 'api/authorization',
  ASSOCIATION_DATA: 'api/companies/associations',
  EXCLUDE_ITEM: 'api/inventory/exclude',
  INCLUDE_ITEM: 'api/inventory/include',
  USER_INFO: 'api/userinfo/me',
  USER_SIGN_OUT: 'api/user/signout'
}

const WizardStepStatus = {
  COMPLETE: 'complete',
  DEFAULT: 'default'
}

const defaultPackageModules = {
  antimalware: true,
  advancedThreatControl: true,
  advancedAntiExploit: true,
  deviceControl: true,
  edrSensor: true,
  encryption: true,
  patchManagement: true,
  firewall: true,
  powerUser: true,
  networkProtection: true,
  contentControl: true,
  networkAttackDefense: true,
  removeCompetitor: true,
  saveAgentNameAsLabel: true
}

const inventorySyncOptions = [
  {
    label: 'Monitor & report synchronized inventory status',
    value: '0',
    description: 'Generates sync events for already synchronized inventory. '
  },
  {
    label: 'Monitor & handle synchronized inventory status',
    value: '2',
    description: 'Generates and handles events for already synchronized inventory. '
  },
  {
    label: 'Automatic inventory synchronization',
    value: '1',
    description: 'Generates and handles sync events for the entire inventory.'
  }
]

const defaultSyncModes = [
  {
    label: 'Include the entire inventory',
    value: 0,
  },
  {
    label: 'Exclude the entire inventory',
    value: 1
  }
]

const ToastSeverity = {
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
  NEUTRAL: 'neutral',
  INFORMATIVE: 'informative',
}

const AgentColumnKey = {
  AGENT_NAME: 'name',
  IP_ADDRESS: 'publicIpAddress',
  OS_INFO: 'description',
  OS: 'type',
  LAST_SEEN_ONLINE: 'lastSeenOnline',
  EXCLUDED: 'excluded',
  INSTALLSTATE: 'installState'
}

const InventoryEventsColumnKey = {
  EVENT_TYPE: 'eventType',
  TYPE: 'entityType',
  SOURCE_NAME: 'rmmItemName',
  DESTINATION_NAME: 'gravityZoneItemName',
  CHILDREN_COUNT: 'childrenCount',
  GENERATED_AT: 'createdDate',
  ACTIONS: 'actions'
}

const AgentEventsColumnKey = {
  EVENT_TYPE: 'eventType',
  SOURCE_NAME: 'rmmEndpointName',
  DESTINATION_NAME: 'gravityZoneEndpointName',
  GENERATED_AT: 'createdDate',
  ACTIONS: 'actions'
}

const ManageAssociationsColumnKey = {
  KASEYA_COMPANY: 'kaseyaCompany',
  GRAVITYZONE_COMPANY: 'gravityZoneCompany',
  TYPE: 'type',
  SECURITY_MANAGED_BY_PARTNER: 'securityManagedByPartner',
  COMPANY_SUSPENDED: 'companySuspended'
}

const networkProtectionSubModules = [
  {
    isWindows: true,
    isMacOS: true,
    isLinux: false,
    moduleAcronym: 'cc',
    label: 'Content Control'
  },
  {
    isWindows: true,
    isMacOS: true,
    isLinux: true,
    moduleAcronym: 'nad',
    label: 'Network Attack Defense'
  },
]

const agentsColumnDetails = [
  {
    column: 0,
    key: AgentColumnKey.AGENT_NAME,
    columnName: 'Kaseya agent',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 1,
    key: AgentColumnKey.EXCLUDED,
    columnName: 'Excluded',
    type: ColumnType.ENUM,
    values: ExcludedValues,
    removable: false,
    editingDisabled: true,
    canCopyContent: false,
  },
  {
    column: 2,
    key: AgentColumnKey.INSTALLSTATE,
    columnName: 'Sync status',
    type: ColumnType.ENUM,
    values: InstallStatesValues,
    removable: false,
    editingDisabled: true,
    canCopyContent: false,
  },
  {
    column: 3,
    key: AgentColumnKey.IP_ADDRESS,
    columnName: 'Public IP address',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 4,
    key: AgentColumnKey.OS_INFO,
    columnName: 'OS info',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 5,
    key: AgentColumnKey.OS,
    columnName: 'OS',
    type: ColumnType.ENUM,
    values: OsType,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 6,
    key: AgentColumnKey.LAST_SEEN_ONLINE,
    columnName: 'Last seen online',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  }
];

const inventoryEventsColumnDetails = [
  {
    column: 0,
    key: InventoryEventsColumnKey.EVENT_TYPE,
    columnName: 'Event type',
    type: ColumnType.ENUM,
    values: InventoryEventLabels,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 1,
    key: InventoryEventsColumnKey.TYPE,
    columnName: 'Type',
    type: ColumnType.ENUM,
    values: InventoryEntities,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 2,
    key: InventoryEventsColumnKey.SOURCE_NAME,
    columnName: 'Source name',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 3,
    key: InventoryEventsColumnKey.DESTINATION_NAME,
    columnName: 'Destination name',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 4,
    key: InventoryEventsColumnKey.CHILDREN_COUNT,
    columnName: 'Children',
    type: ColumnType.NUMBER,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 5,
    key: InventoryEventsColumnKey.GENERATED_AT,
    columnName: 'Generated at',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 6,
    key: InventoryEventsColumnKey.ACTIONS,
    columnName: 'Action',
    type: ColumnType.ACTION,
    removable: false
  }
];

const agentEventsColumnDetails = [
  {
    column: 0,
    key: AgentEventsColumnKey.EVENT_TYPE,
    columnName: 'Event type',
    type: ColumnType.ENUM,
    values: AgentInventoryEventLabels,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 1,
    key: AgentEventsColumnKey.SOURCE_NAME,
    columnName: 'Kaseya agent',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 2,
    key: AgentEventsColumnKey.DESTINATION_NAME,
    columnName: 'Bitdefender agent',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  },
  {
    column: 3,
    key: AgentEventsColumnKey.GENERATED_AT,
    columnName: 'Generated at',
    type: ColumnType.STRING,
    removable: false,
    editingDisabled: true,
    canCopyContent: true,
  }
];

const inventoryEventFilters = [
  { eventType: { in: [] } },
  { entityType: { in : [] } },
  { rmmItemName: { contains: '' } },
  { gravityZoneItemName: { contains: '' } }
];

const agentEventFilters = [
  { eventType: { in: [] } },
  { rmmEndpointName: { contains: '' } },
  { gravityZoneEndpointName: { contains: '' } }
];


const agentFilters = [
  { name: { contains: '' } },
  { excluded: { in: [] } },
  { description: { contains: '' } },
  { type: { in: [] } },
  { publicIpAddress: { contains: '' } },
  { installState: { in: [] } },
];

const manageAssociationsFilters = [
  { kaseyaCompany: { contains: '' } },
  { gravityZoneCompany: { contains: ''} }
];

const InventorySyncLevels = [
  {
    label: 'Monitor & report synchronized inventory status',
    value: 0
  },
  {
    label: 'Monitor & handle synchronized inventory status',
    value: 2
  },
  {
    label: 'Automatic inventory synchronization',
    value: 1
  }
]

const InventorySyncDefaultMode = [
  {
    label: 'Include entire inventory',
    value: false
  },
  {
    label: 'Exclude entire inventory',
    value: true
  }
]

const UsePrefixForGenesisMode = [
  {
    label: 'Always',
    value: 0
  },
  {
    label: 'Only if the name already exists',
    value: 1
  }
]

const EventHandlingOptions = [
  {
    label: 'Manual',
    value: false
  },
  {
    label: 'Automatic',
    value: true
  }
]

const SynchronizeInventorySettings = (props) => {
  return {
    synchronizeInventoryMode: props.synchronizeInventoryMode,
    destinationMovedAuto: props.destinationMovedAuto,
    destinationDeletedAuto: props.destinationDeletedAuto,
    missingAssociationFoundAuto: props.missingAssociationFoundAuto,
    eligibleForUpgradeAuto: props.eligibleForUpgradeAuto,
    sourceGoneSuperNovaAuto: props.sourceGoneSuperNovaAuto,
    inventorySyncReversed: props.inventorySyncReversed,

    createGroups: props.createGroups,
    packageConfigurationLevel: props.packageConfigurationLevel,
    featuresSyncMode: props.featuresSyncMode,
    companyPackageNameTemplate: props.companyPackageNameTemplate,
    groupPackageNameTemplate: props.groupPackageNameTemplate
  }
}

const PrefixSettingsModel = (props) => {
  return {
    prefix: props.prefix,
    usePrefixForTakeover: props.usePrefixForTakeover,
    usePrefixForGenesis: props.usePrefixForGenesis,
    usePrefixForGenesisMode: props.usePrefixForGenesisMode
  }
}

const GravityZoneSettingsModel = (props) => {
  return {
    gzApiKey: props.gzApiKey,
    gzServerAddress: props.gzServerAddress
  }
}

const InventoryTenant = (props) => {
  return {
    gravityZoneSettingsModel: GravityZoneSettingsModel(props.gzSettings),
    eventAndSyncSettingsModel: SynchronizeInventorySettings(props.inventorySyncSettings),
    prefixSettingsModel: PrefixSettingsModel(props.prefixSettings)
  }
}

const parseFiltersToQuery = (filters) => {
  if (!filters) {
    return;
  }

  let queryStatement = '';

  for (let index = 0; index < filters.length; index++) {
    const filter = filters[index];
    let filterAsQuery = '';
    
    // The excluded filter is treated separately
    if (Object.entries(filter)[0][0] === "excluded") {
      continue
    }

    // The install filter is treated separately
    if (Object.entries(filter)[0][0] === "installState") {
      continue
    }

    let key = Object.entries(filter)[0][1];
    let value = '';

    if (Object.keys(key)[0] === 'startsWith') {
      value = key['startsWith'];

      if (index + 1 === filters.length) {
        filterAsQuery = ` startswith(tolower(${Object.keys(filter)[0]}), tolower('${value}'))`;
      } else {
        filterAsQuery = `startswith(tolower(${Object.keys(filter)[0]}), tolower('${value}')) and `;
      }
      queryStatement = queryStatement.concat(filterAsQuery);

    }

    if (Object.keys(key)[0] === 'in') {
      value = key['in']
      if (value.length !== 0) {
        if (index + 1 === filters.length) {
          filterAsQuery = `${Object.keys(filter)[0]} in [${value.map(i => `'${i}'`)}]`;
        } else {
          filterAsQuery = `${Object.keys(filter)[0]} in [${value.map(i => `'${i}'`)}] and `;
        }

        queryStatement = queryStatement.concat(filterAsQuery);
      }
    }
  }

  if (queryStatement.endsWith(' ')) {
    return queryStatement.slice(0, -4);
  }

  return queryStatement;
}

const elementClassListToggle = (element, className) => {
  if (!element.classList.contains(className)) {
    element.classList.add(className);
  } else {
    element.classList.remove(className);
  }
};

const InventorySyncOperationOptions = (companyIdToAssociate, doCreateGroups, packageConfigurationLevel, featuresSyncMode) => {
  return {
    AssociateWithCompanyId: companyIdToAssociate,
    CreateGroups: doCreateGroups,
    PackageConfigurationLevel: packageConfigurationLevel,
    FeaturesSyncMode: featuresSyncMode
  }
}

const InventoryOnDemandSyncDto = (operationType, itemType, itemId, operationOptions, productType) => {
  return {
    Operations: [{
      OperationType: operationType,
      ItemType: itemType,
      ItemId: itemId,
      Options: operationOptions
    }],
    ProductType: productType
  }
}

const radioOptionsAgentSync = [
  {
    label: 'Monitor & report synchronized agent status',
    value: '0',
    description: 'Generates events for synchronized agents.'
  },
  {
    label: 'Monitor & handle synchronized agent status',
    value: '2',
    description: 'Generates and handles events for synchronized agents.'
  },
  {
    label: 'Automatic agent synchronization',
    value: '1',
    description: 'Generates and handles events for all agents, both synchronized and not synchronized.'
  }
]

const configurationWizardOptions = [
  {
    id: "companyDetails",
    title: "My company",
    step: 1,
    isOptional: false
  },
  {
    id: "inventorySynchronization",
    title: "Inventory synchronization",
    step: 2,
    isOptional: false
  },
  {
    id: "agentSynchronization",
    title: "Agent synchronization",
    step: 3,
    isOptional: false
  },
  {
    id: "finalize",
    title: "Finalize",
    step: 4,
    isOptional: false
  }
]

const LocationPaths = {
  INVENTORY_EVENTS: '/inventoryevents',
  DASHBOARD: '/dashboard',
  ROOT: '/',
  CONFIGURATION: '/configuration',
  INVENTORY: '/inventory',
  AGENT_EVENTS: '/agentevents',
  MANAGE_ASSOCIATIONS: '/manageassociations',
  UNAUTHORIZED: '/unauthorized'
}

const IntegrationStatusDescription = {
  UP_AND_RUNNING: {
    description: 'All systems up & running'
  },
  TASKS_FAILED: {
    description: 'Last inventory or agent task failed'
  },
  EVENTS_NOT_HANDLED: {
    description: 'Synchronization events not handled'
  }
}

const InventoryOperations = {
  createNewCompanyAction: {
    label: 'Set as a new GravityZone company',
    value: OperationType.SYNC
  },
  associateAction: {
    label: 'Associate',
    value: OperationType.ASSOCIATE
  },
  deleteAssociationAction: {
    label: 'Delete association',
    value: OperationType.DELETE_ASSOCIATION
  }
};

const publicKbUrl = 'https://www.bitdefender.com/business/support/en/77211-959269-kaseya-vsa-x.html';

function objectEquals(x, y) {
  'use strict';

  if (x === null || x === undefined || y === null || y === undefined) { return x === y; }
  // after this just checking type of one would be enough
  if (x.constructor !== y.constructor) { return false; }
  // if they are functions, they should exactly refer to same one (because of closures)
  if (x instanceof Function) { return x === y; }
  // if they are regexps, they should exactly refer to same one (it is hard to better equality check on current ES)
  if (x instanceof RegExp) { return x === y; }
  if (x === y || x.valueOf() === y.valueOf()) { return true; }
  if (Array.isArray(x) && x.length !== y.length) { return false; }

  // if they are dates, they must had equal valueOf
  if (x instanceof Date) { return false; }

  // if they are strictly equal, they both need to be object at least
  if (!(x instanceof Object)) { return false; }
  if (!(y instanceof Object)) { return false; }

  // recursive object equality check
  var p = Object.keys(x);
  return Object.keys(y).every(function (i) { return p.indexOf(i) !== -1; }) &&
    p.every(function (i) { return objectEquals(x[i], y[i]); });
}

const isDeepEqual = (object1, object2) => {
  const objKeys1 = Object.keys(object1);

  for (let key of objKeys1) {
    const value1 = object1[key];
    const value2 = object2[key];

    const areObjects = isObject(value1) && isObject(value2);

    if ((areObjects && !isDeepEqual(value1, value2)) ||
      (!areObjects && value1 !== value2)
    ) {
      return false;
    }
  }
  return true;
};

const isObject = (object) => {
  return Object.prototype.toString.call(object) === "[object Object]";
};

const formatDate = (newDate, isDateOnly = false) => {
  const datetimeTitleFormat = 'MMM DD, YYYY hh:mm A';
  const dateTitleFormat = 'MM/DD/YYYY';
  if (isDateOnly) {
    newDate = moment.utc(newDate).local().format(dateTitleFormat);
    return newDate;
  }
  newDate = moment.utc(newDate).local().format(datetimeTitleFormat);
  return newDate;
}

function renderHTMLToDOMElements(htmlCode) {
  return document.createRange().createContextualFragment(HtmlEscapeUtils.htmlDecode(htmlCode));
}

  const GridEvents = {
  GRID_STARTED : 'gridStarted',
  LOAD_MORE_ROWS : 'loadMoreRows',
  LOAD_FIRST_PAGE : 'loadFirstPage',
  LOAD_LAST_PAGE : 'loadLastPage',
  LOAD_NEXT_PAGE : 'loadNextPage',
  LOAD_PREVIOUS_PAGE : 'loadPreviousPage',
  SET_PAGE_NUMBER : 'setPageNumber',
  REFRESH_DATA_FROM_FOOTER : 'refreshDataFromFooter',
  BACK_TO_TOP : 'backToTop',
  SET_PAGE_SIZE : 'setPageSize',
  SELECT_ROWS : 'selectRows',
  EDIT_ROW : 'editRow',
  EDIT_ROW_ENABLED : 'editRowEnabled',
  EDIT_ROW_SAVE : 'editRowSave',
  EDIT_ROW_CANCEL : 'editRowCancel',
  ROWS_LOADING_FINISHED : 'rowsLoadingFinished',
  ROWS_LOADING_STARTED : 'rowsLoadingStarted',
  ROW_CLICKED : 'rowClicked',
  ROW_RIGHT_CLICKED : 'rowRightClicked',
  GRID_SETTINGS_CREATED : 'gridSettingsCreated',
  EXPORT_CSV : 'exportCsv',
  GRID_SORTED : 'gridSorted',
  GRID_SELECT_ALL : 'gridSelectAll',
  COLUMNS_REORDERED : 'columnsReordered',
}

const ScanTypes = [
  {
    value: "full",
    title: "Full scan"
  },
  {
    value: "quick",
    title: "Quick scan",
  }
]

const ScanTypeValues = {
  FULL: 'full',
  Quick: 'quick'
}

export {
  ApiEndpoints,
  agentsColumnDetails,
  AgentColumnKey,
  configurationWizardOptions,
  defaultSyncModes,
  elementClassListToggle,
  EventHandlingOptions,
  agentFilters,
  InventoryOnDemandSyncDto,
  InventorySyncDefaultMode,
  InventorySyncLevels,
  InventorySyncOperationOptions,
  inventorySyncOptions,
  InventoryTenant,
  networkProtectionSubModules,
  objectEquals,
  parseFiltersToQuery,
  radioOptionsAgentSync,
  ToastSeverity,
  UsePrefixForGenesisMode,
  WizardStepStatus,
  defaultPackageModules,
  inventoryEventFilters,
  inventoryEventsColumnDetails,
  InventoryEventsColumnKey,
  isDeepEqual,
  LocationPaths,
  formatDate,
  IntegrationStatusDescription,
  agentEventFilters,
  AgentEventsColumnKey,
  agentEventsColumnDetails,
  ManageAssociationsColumnKey,
  manageAssociationsFilters,
  renderHTMLToDOMElements,
  GridEvents,
  InventoryOperations,
  publicKbUrl,
  ScanTypes,
  ScanTypeValues
};